import React, { useContext } from 'react'
import { Field, Dropdown, Option, OptionGroup } from '@fluentui/react-components'
import { Control, Controller, UseFormWatch } from 'react-hook-form'
import { Components, DemandComplianceModuleDefinition, Modules, OrganisationWithUserAccountsDTO, RiskManagementModules } from '../../api/schemas/schema'
import { t } from 'i18next'
import { DataContext } from '../../App'
interface ModuleDropdownOption {
   key: string
   text: string
   moduleType: 'Component' | 'DemandComplianceModule' | 'RiskManagementModule'
   disabled?: boolean
}
type ControlledModulesDropDownProps = {
   name: any
   control: Control<any>
   label: string
   watch: UseFormWatch<OrganisationWithUserAccountsDTO>
   disabled?: boolean
}
export const ControlledModulesDropDown = ({ name, control, label, watch, disabled }: ControlledModulesDropDownProps) => {
   const dataContext = useContext(DataContext)
   const { state } = dataContext
   const dynamicModules = state.demandComplianceModuleDefinitions || []

   const generateModuleOptions = (dynamicModules: DemandComplianceModuleDefinition[]): ModuleDropdownOption[] => {
      const riskManagementOptions = Object.entries(RiskManagementModules)
         .filter(([key, value]) => !isNaN(Number(value)))
         .map(([key, value]) => ({
            key: `RiskManagementModule-${value}`,
            text: key,
            moduleType: 'RiskManagementModule' as const,
         }))
      const componentOptions = Object.entries(Components)
         .filter(([key, value]) => !isNaN(Number(value)))
         .map(([key, value]) => ({
            key: `Component-${value}`,
            text: key,
            moduleType: 'Component' as const,
         }))

      const demandOptions = dynamicModules
         .filter((m) => m.active) // Only show active modules
         .map((m) => ({
            key: `DemandComplianceModule-${m.id}`,
            text: m.name,
            moduleType: 'DemandComplianceModule' as const,
         }))

      return [...componentOptions, ...demandOptions, ...riskManagementOptions]
   }

   const defaultValues =
      watch('organisation.modules')
         ?.map((module: Modules) => {
            if (module.component !== undefined && module.component !== null) {
               return {
                  key: `Component-${module.component}`,
                  text: Components[module.component],
                  moduleType: 'Component' as const,
               }
            }
            if (module.demandComplianceModuleDefinitionId) {
               const meta = dynamicModules.find((m) => m.id === module.demandComplianceModuleDefinitionId)
               return {
                  key: `DemandComplianceModule-${module.demandComplianceModuleDefinitionId}`,
                  text: meta?.name || 'Unknown Module',
                  moduleType: 'DemandComplianceModule' as const,
               }
            }
            if (module.riskManagementModule !== undefined && module.riskManagementModule !== null) {
               return {
                  key: `RiskManagementModule-${module.riskManagementModule}`,
                  text: RiskManagementModules[module.riskManagementModule],
                  moduleType: 'RiskManagementModule' as const,
               }
            }
            return null
         })
         .filter(Boolean) ?? []

   const riskManagementComponentKey = `Component-${Components.RISK_MANAGEMENT}`
   const isRiskManagementSelected = defaultValues.some((v) => v?.key === riskManagementComponentKey)

   const demandComplianceComponentKey = `Component-${Components.DEMAND_COMPLIANCE}`
   const isDemandComplianceSelected = defaultValues.some((v) => v?.key === demandComplianceComponentKey)

   const groupedOptions = generateModuleOptions(dynamicModules).reduce(
      (groups, option) => {
         if (!groups[option.moduleType]) {
            groups[option.moduleType] = []
         }
         groups[option.moduleType].push(option)
         return groups
      },
      {} as Record<string, ModuleDropdownOption[]>
   )
   return (
      <Controller
         name={name}
         control={control}
         render={({ field: { value, onChange } }) => {
            const selectedOptions = defaultValues?.map((v: any) => v.key).filter(Boolean) ?? []
            return (
               <Field label={label}>
                  <Dropdown
                     multiselect
                     value={defaultValues.map((opt) => t(opt.text)).join(', ')}
                     selectedOptions={selectedOptions}
                     onOptionSelect={(e, d) => {
                        const currentModules = watch('organisation.modules') ?? []

                        // Inside onOptionSelect
                        const transformedModules = d.selectedOptions
                           .map((key: string) => {
                              const [moduleType, ...idParts] = key.split('-')
                              const id = idParts.join('-')
                              if (moduleType === 'RiskManagementModule' && !isRiskManagementSelected) {
                                 return null
                              }

                              if (moduleType === 'DemandComplianceModule' && !isDemandComplianceSelected) {
                                 return null
                              }

                              const existing = currentModules.find((m: any) => {
                                 if (moduleType === 'Component') return m.component === Number(id)
                                 if (moduleType === 'DemandComplianceModule') return m.demandComplianceModuleDefinitionId === id
                                 if (moduleType === 'RiskManagementModule') return m.riskManagementModule === Number(id)
                                 return false
                              })

                              if (existing) return existing

                              // IMPORTANT: These keys must match your "export interface Modules" exactly
                              if (moduleType === 'Component') {
                                 return { id: null, isActivated: true, component: Number(id) } as any
                              }
                              if (moduleType === 'DemandComplianceModule') {
                                 return { id: null, isActivated: true, demandComplianceModuleDefinitionId: id } as any
                              }
                              if (moduleType === 'RiskManagementModule') {
                                 return { id: null, isActivated: true, riskManagementModule: Number(id) } as any
                              }
                              return null
                           })
                           .filter(Boolean)
                        onChange(transformedModules)
                     }}
                     disabled={disabled}
                  >
                     {Object.entries(groupedOptions).map(([moduleType, options]) => {
                        if (moduleType === 'RiskManagementModule' && !isRiskManagementSelected) {
                           return null
                        }

                        if (moduleType === 'DemandComplianceModule' && !isDemandComplianceSelected) {
                           return null
                        }

                        return (
                           <OptionGroup key={moduleType} label={t(moduleType)}>
                              {options.map((option) => (
                                 <Option key={option.key} value={option.key}>
                                    {t(option.text)}
                                 </Option>
                              ))}
                           </OptionGroup>
                        )
                     })}
                  </Dropdown>
               </Field>
            )
         }}
      />
   )
}
